import React, { useState, useEffect } from 'react';
import api from '../services/api';

const SimilarityIndex = ({ crop }) => {
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [genotypes, setGenotypes] = useState([]);
  const [selectedGenotype, setSelectedGenotype] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  
  // New states for file upload comparison
  const [mode, setMode] = useState('genotype'); // 'genotype' or 'file'
  const [uploadFile, setUploadFile] = useState(null);
  const [masterTableForFile, setMasterTableForFile] = useState('');
  const [fileResults, setFileResults] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);

  // Fetch tables on component mount
  useEffect(() => {
    fetchTables();
  }, [crop]);

  // Fetch genotypes when table is selected
  useEffect(() => {
    if (selectedTable) {
      fetchGenotypes(selectedTable);
      setResults(null);
      setSelectedGenotype('');
    }
  }, [selectedTable]);

  const fetchTables = async () => {
    try {
      setLoading(true);
      setError('');
      let url = '/api/similarity/tables';
      if (crop) url += `?crop=${encodeURIComponent(crop)}`;
      const response = await api.get(url);
      setTables(response.data.tables);
    } catch (err) {
      setError('Failed to fetch tables: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchGenotypes = async (tableName) => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/api/similarity/tables/${tableName}/genotypes`);
      setGenotypes(response.data.genotypes);
    } catch (err) {
      setError('Failed to fetch genotypes: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const analyzeGenotypeSimilarity = async () => {
    if (!selectedTable || !selectedGenotype) {
      setError('Please select both a table and a genotype');
      return;
    }

    try {
      setAnalyzing(true);
      setError('');
      setResults(null);

      const response = await api.post('/api/similarity/analyze-genotypes', {
        table_name: selectedTable,
        genotype_name: selectedGenotype
      }, {
        timeout: 600000  // 10 minutes for large datasets
      });

      setResults(response.data);
    } catch (err) {
      setError('Analysis failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setAnalyzing(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        setError('Please upload a CSV file');
        return;
      }
      setUploadFile(file);
      setFileResults(null);
      setError('');
    }
  };

  const compareFileWithMaster = async () => {
    if (!uploadFile || !masterTableForFile) {
      setError('Please select both a file and a master table');
      return;
    }

    try {
      setUploadingFile(true);
      setError('');
      setFileResults(null);

      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('master_table', masterTableForFile);

      const response = await api.post('/api/similarity/upload-compare', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setFileResults(response.data);
    } catch (err) {
      setError('File comparison failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploadingFile(false);
    }
  };

  // Filter genotypes based on search term
  const filteredGenotypes = genotypes.filter(g =>
    g.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get percentage color based on value
  const getPercentageColor = (percentage) => {
    if (percentage >= 80) return '#025c4f';
    if (percentage >= 60) return '#2d7a6e';
    if (percentage >= 40) return '#5a998d';
    return '#8bb8ad';
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>🔍 Similarity Index</h2>
      
      <div style={styles.infoBox}>
        <strong>About:</strong> Analyze genotype similarity by comparing marker values across genotypes.
        Select a genotype to find which other genotypes share the most similar marker profiles.
      </div>

      {error && (
        <div style={styles.errorBox}>
          {error}
        </div>
      )}

      {/* Mode Selection */}
      <div style={styles.modeSelector}>
        <button
          onClick={() => {
            setMode('genotype');
            setFileResults(null);
            setResults(null);
            setError('');
          }}
          style={{
            ...styles.modeButton,
            ...(mode === 'genotype' ? styles.modeButtonActive : {})
          }}
        >
          🧬 Genotype Similarity
        </button>
        <button
          onClick={() => {
            setMode('file');
            setFileResults(null);
            setResults(null);
            setError('');
          }}
          style={{
            ...styles.modeButton,
            ...(mode === 'file' ? styles.modeButtonActive : {})
          }}
        >
          📁 Upload & Compare File
        </button>
      </div>

      {/* Genotype Similarity Mode */}
      {mode === 'genotype' && (
        <>
          {/* Table Selection */}
          <div style={styles.section}>
            <label style={styles.label}>
              <strong>1. Select Table:</strong>
            </label>
            <select
              value={selectedTable}
              onChange={(e) => setSelectedTable(e.target.value)}
              style={styles.select}
              disabled={loading}
            >
              <option value="">-- Select a table --</option>
              {tables.map(table => (
                <option key={table} value={table}>
                  {table}
                </option>
              ))}
            </select>
          </div>

          {/* Genotype Selection */}
          {selectedTable && (
            <div style={styles.section}>
              <label style={styles.label}>
                <strong>2. Select Genotype:</strong>
              </label>
              
              {/* Search Box */}
              <input
                type="text"
                placeholder="Search genotypes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.searchInput}
              />

              {/* Genotype List */}
              <div style={styles.columnList}>
                {loading ? (
                  <div style={styles.loadingText}>Loading genotypes...</div>
                ) : filteredGenotypes.length === 0 ? (
                  <div style={styles.noResults}>
                    {searchTerm ? 'No genotypes match your search' : 'No genotypes available'}
                  </div>
                ) : (
                  filteredGenotypes.map(g => (
                    <div
                      key={g}
                      onClick={() => setSelectedGenotype(g)}
                      style={{
                        ...styles.columnItem,
                        ...(selectedGenotype === g ? styles.columnItemSelected : {})
                      }}
                    >
                      {g}
                      {selectedGenotype === g && (
                        <span style={styles.checkmark}>✓</span>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Analyze Button */}
          {selectedGenotype && (
            <div style={styles.section}>
              <button
                onClick={analyzeGenotypeSimilarity}
                disabled={analyzing}
                style={styles.analyzeButton}
              >
                {analyzing ? '⏳ Analyzing... (this may take a moment for large datasets)' : '🧬 Find Similar Genotypes'}
              </button>
              {analyzing && (
                <div style={{ marginTop: '15px', textAlign: 'center', color: '#666' }}>
                  <div style={{
                    width: '100%', height: '6px', backgroundColor: '#e0e0e0',
                    borderRadius: '3px', overflow: 'hidden', marginTop: '10px'
                  }}>
                    <div style={{
                      width: '100%', height: '100%', backgroundColor: '#025c4f',
                      borderRadius: '3px',
                      animation: 'pulse 1.5s ease-in-out infinite',
                    }} />
                  </div>
                  <style>{`@keyframes pulse { 0%, 100% { opacity: 0.3; } 50% { opacity: 1; } }`}</style>
                  <p style={{ marginTop: '8px', fontSize: '14px' }}>
                    Comparing marker profiles across all genotypes...
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Results Display */}
          {results && (
            <div style={styles.resultsSection}>
              <h3 style={styles.resultsHeading}>
                Most Similar to: <span style={styles.columnName}>{results.selected_genotype}</span>
              </h3>
              
              <div style={styles.resultsSummary}>
                Table: <strong>{results.table_name}</strong> | 
                Genotypes Compared: <strong>{results.total_genotypes_compared}</strong> |
                Markers Used: <strong>{results.total_markers_used}</strong>
              </div>

              {results.top_matches.length === 0 ? (
                <div style={styles.noMatches}>
                  No similar genotypes found.
                </div>
              ) : (
                <div style={styles.tableContainer}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Rank</th>
                        <th style={styles.th}>Genotype</th>
                        <th style={styles.th}>Similarity %</th>
                        <th style={styles.th}>Matching Markers</th>
                        <th style={styles.th}>Total Markers</th>
                        <th style={styles.th}>Visual</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.top_matches.map((match, index) => (
                        <tr key={match.genotype_name} style={styles.tr}>
                          <td style={styles.td}>{index + 1}</td>
                          <td style={styles.td}>
                            <strong>{match.genotype_name}</strong>
                          </td>
                          <td style={styles.td}>
                            <span style={{
                              ...styles.percentageBadge,
                              backgroundColor: getPercentageColor(match.match_percentage)
                            }}>
                              {match.match_percentage}%
                            </span>
                          </td>
                          <td style={styles.td}>{match.matching_markers}</td>
                          <td style={styles.td}>{match.total_markers}</td>
                          <td style={styles.td}>
                            <div style={styles.progressBarContainer}>
                              <div
                                style={{
                                  ...styles.progressBarFill,
                                  width: `${match.match_percentage}%`,
                                  backgroundColor: getPercentageColor(match.match_percentage)
                                }}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* File Upload Mode */}
      {mode === 'file' && (
        <>
          <div style={styles.section}>
            <label style={styles.label}>
              <strong>1. Select Master Table:</strong>
            </label>
            <select
              value={masterTableForFile}
              onChange={(e) => setMasterTableForFile(e.target.value)}
              style={styles.select}
              disabled={loading}
            >
              <option value="">-- Select a master table --</option>
              {tables.map(table => (
                <option key={table} value={table}>
                  {table}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.section}>
            <label style={styles.label}>
              <strong>2. Upload CSV File:</strong>
            </label>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              style={styles.fileInput}
            />
            {uploadFile && (
              <div style={styles.fileInfo}>
                ✓ Selected: <strong>{uploadFile.name}</strong>
              </div>
            )}
          </div>

          {uploadFile && masterTableForFile && (
            <div style={styles.section}>
              <button
                onClick={compareFileWithMaster}
                disabled={uploadingFile}
                style={styles.analyzeButton}
              >
                {uploadingFile ? '⏳ Comparing...' : '🔍 Compare File with Master'}
              </button>
            </div>
          )}

          {/* File Comparison Results */}
          {fileResults && (
            <div style={styles.resultsSection}>
              <h3 style={styles.resultsHeading}>
                File Comparison Results
              </h3>
              
              <div style={styles.resultsSummary}>
                File: <strong>{fileResults.file_name}</strong> | 
                Master Table: <strong>{fileResults.master_table}</strong> | 
                File Rows: <strong>{fileResults.file_row_count}</strong> |
                Master Rows: <strong>{fileResults.master_row_count}</strong>
              </div>

              {fileResults.file_columns.map((fileCol, idx) => {
                const matches = fileResults.top_matches_per_column[fileCol] || [];
                
                return (
                  <div key={idx} style={styles.fileColumnSection}>
                    <h4 style={styles.fileColumnHeading}>
                      File Column: <span style={styles.columnName}>{fileCol}</span>
                    </h4>
                    
                    {matches.length === 0 ? (
                      <div style={styles.noMatches}>
                        No matches found for this column
                      </div>
                    ) : (
                      <div style={styles.tableContainer}>
                        <table style={styles.table}>
                          <thead>
                            <tr>
                              <th style={styles.th}>Rank</th>
                              <th style={styles.th}>Master Column</th>
                              <th style={styles.th}>Match %</th>
                              <th style={styles.th}>Matching Rows</th>
                              <th style={styles.th}>Valid Rows</th>
                              <th style={styles.th}>Visual</th>
                            </tr>
                          </thead>
                          <tbody>
                            {matches.map((match, index) => (
                              <tr key={match.master_column} style={styles.tr}>
                                <td style={styles.td}>{index + 1}</td>
                                <td style={styles.td}>
                                  <strong>{match.master_column}</strong>
                                </td>
                                <td style={styles.td}>
                                  <span style={{
                                    ...styles.percentageBadge,
                                    backgroundColor: getPercentageColor(match.match_percentage)
                                  }}>
                                    {match.match_percentage}%
                                  </span>
                                </td>
                                <td style={styles.td}>{match.matching_rows}</td>
                                <td style={styles.td}>{match.total_valid_rows}</td>
                                <td style={styles.td}>
                                  <div style={styles.progressBarContainer}>
                                    <div
                                      style={{
                                        ...styles.progressBarFill,
                                        width: `${match.match_percentage}%`,
                                        backgroundColor: getPercentageColor(match.match_percentage)
                                      }}
                                    />
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '30px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  heading: {
    color: '#025c4f',
    fontSize: '28px',
    marginBottom: '20px',
    borderBottom: '3px solid #025c4f',
    paddingBottom: '10px',
  },
  infoBox: {
    backgroundColor: '#dbf8f4',
    border: '1px solid #8bcec0',
    borderRadius: '8px',
    padding: '15px',
    marginBottom: '25px',
    color: '#025c4f',
    lineHeight: '1.6',
  },
  errorBox: {
    backgroundColor: '#fee',
    border: '1px solid #fcc',
    borderRadius: '8px',
    padding: '15px',
    marginBottom: '20px',
    color: '#c33',
  },
  modeSelector: {
    display: 'flex',
    gap: '15px',
    marginBottom: '30px',
  },
  modeButton: {
    flex: 1,
    padding: '15px',
    fontSize: '16px',
    fontWeight: 'bold',
    border: '2px solid #ddd',
    borderRadius: '8px',
    backgroundColor: 'white',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  modeButtonActive: {
    backgroundColor: '#025c4f',
    color: 'white',
    borderColor: '#025c4f',
  },
  section: {
    marginBottom: '25px',
  },
  label: {
    display: 'block',
    marginBottom: '10px',
    fontSize: '16px',
    color: '#333',
  },
  select: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '2px solid #ddd',
    borderRadius: '6px',
    backgroundColor: 'white',
    cursor: 'pointer',
  },
  fileInput: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '2px solid #ddd',
    borderRadius: '6px',
    backgroundColor: 'white',
    cursor: 'pointer',
  },
  fileInfo: {
    marginTop: '10px',
    padding: '10px',
    backgroundColor: '#dbf8f4',
    border: '1px solid #8bcec0',
    borderRadius: '6px',
    color: '#025c4f',
  },
  searchInput: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '2px solid #ddd',
    borderRadius: '6px',
    marginBottom: '15px',
    boxSizing: 'border-box',
  },
  columnList: {
    maxHeight: '300px',
    overflowY: 'auto',
    border: '2px solid #ddd',
    borderRadius: '6px',
    backgroundColor: 'white',
  },
  columnItem: {
    padding: '12px 15px',
    cursor: 'pointer',
    borderBottom: '1px solid #eee',
    transition: 'background-color 0.2s',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  columnItemSelected: {
    backgroundColor: '#dbf8f4',
    color: '#025c4f',
    fontWeight: 'bold',
    borderLeft: '4px solid #025c4f',
  },
  checkmark: {
    color: '#025c4f',
    fontSize: '18px',
    fontWeight: 'bold',
  },
  loadingText: {
    padding: '20px',
    textAlign: 'center',
    color: '#888',
  },
  noResults: {
    padding: '20px',
    textAlign: 'center',
    color: '#888',
  },
  analyzeButton: {
    backgroundColor: '#025c4f',
    color: 'white',
    border: 'none',
    padding: '14px 30px',
    fontSize: '16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 'bold',
    transition: 'background-color 0.3s',
  },
  resultsSection: {
    marginTop: '40px',
    backgroundColor: '#f9f9f9',
    padding: '25px',
    borderRadius: '8px',
    border: '1px solid #ddd',
  },
  resultsHeading: {
    color: '#025c4f',
    fontSize: '22px',
    marginBottom: '15px',
  },
  columnName: {
    backgroundColor: '#dbf8f4',
    padding: '4px 12px',
    borderRadius: '4px',
    fontFamily: 'monospace',
  },
  resultsSummary: {
    marginBottom: '20px',
    padding: '12px',
    backgroundColor: 'white',
    borderRadius: '6px',
    border: '1px solid #ddd',
  },
  fileColumnSection: {
    marginBottom: '30px',
    padding: '20px',
    backgroundColor: 'white',
    borderRadius: '6px',
    border: '1px solid #ddd',
  },
  fileColumnHeading: {
    color: '#2d7a6e',
    fontSize: '18px',
    marginBottom: '15px',
  },
  noMatches: {
    padding: '30px',
    textAlign: 'center',
    color: '#888',
    fontSize: '16px',
  },
  tableContainer: {
    overflowX: 'auto',
    backgroundColor: 'white',
    borderRadius: '6px',
    border: '1px solid #ddd',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '15px',
  },
  th: {
    backgroundColor: '#025c4f',
    color: 'white',
    padding: '14px',
    textAlign: 'left',
    fontWeight: 'bold',
    fontSize: '14px',
  },
  tr: {
    borderBottom: '1px solid #eee',
  },
  td: {
    padding: '14px',
    textAlign: 'left',
  },
  percentageBadge: {
    display: 'inline-block',
    padding: '6px 12px',
    borderRadius: '20px',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '14px',
  },
  progressBarContainer: {
    width: '100%',
    height: '20px',
    backgroundColor: '#eee',
    borderRadius: '10px',
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    transition: 'width 0.3s ease',
    borderRadius: '10px',
  },
};

export default SimilarityIndex;
